import {
  phaseLabel,
  readBondLifecycle,
  type BondPhaseKey,
  type BondPhaseState,
} from "@/lib/bondStateModel";
import { formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S414, BRK-S502 — what the submission page's Bond Lifecycle tracker shows,
// read off the order view. The PHASE, each phase's STATE, its date and its
// sub-label are PMS's (`lifecycle`, PMS-S837 — read ahead of the pin by
// `readBondLifecycle`); none is computed here and nothing reads
// `workflowStatus` (CLAUDE.md #7).
//
// The issuance hold is PMS's too: the wireframe's sub-labels ("On hold · GIA
// Form needed", "On hold · Program + GIA Form needed") are the served `note`
// (BRK-S502). The tracker used to put its own hold, read from the Required
// steps rows, in place of the note; that panel has left the page and the
// client no longer words a hold the server did not send.

/** One column of the tracker. */
export interface TrackerPhase {
  phase: BondPhaseKey;
  label: string;
  /** null for a state this build has not adopted: rendered neutrally. */
  state: BondPhaseState | null;
  /** The phase `lifecycle.phase` names — the "Now" tag. */
  isNow: boolean;
  /** The sub-label: PMS's note, or "Not needed" on a skipped phase PMS left
   *  unworded. */
  note: string | null;
  /** True when PMS's note is an issuance hold ("On hold · …"): the column
   *  draws it in the warning colour. Presentation of the served words only. */
  hold: boolean;
  /** `at` as m/d/yyyy (D-10), only when the note does not already say it. */
  date: string | null;
  /** `at` as served (ISO), for `<time dateTime>`. */
  dateIso: string | null;
}

/**
 * The tracker's columns, in lifecycle order, or null when the order view's
 * `lifecycle` is not the published shape (the box then says so).
 */
export function lifecycleTrackerPhases(
  rawLifecycle: unknown,
): TrackerPhase[] | null {
  const lifecycle = readBondLifecycle(rawLifecycle);
  if (lifecycle === null) return null;
  return lifecycle.phases.map((p) => {
    const note = p.note ?? (p.state === "skipped" ? "Not needed" : null);
    const date = p.at === null ? null : formatBondDate(p.at);
    // PMS's notes usually carry their own date ("Issued 2/4/2026"); saying it
    // twice in a narrow column helps nobody.
    const showDate = date !== null && !(note ?? "").includes(date);
    return {
      phase: p.phase,
      label: phaseLabel(p.phase),
      state: p.state,
      isNow: lifecycle.phase === p.phase,
      note,
      hold: note !== null && note.startsWith("On hold"),
      date: showDate ? date : null,
      dateIso: showDate ? p.at : null,
    };
  });
}

/**
 * PMS's hold on the bid bond, as served — the bid phase's note while the job
 * is in the bid phase and the note is a hold ("On hold · GIA Form needed").
 * The current-bond card and the Bonds table show it in place of the bid
 * bond's own status (BRK-S502). Null otherwise.
 */
export function bidHoldNote(rawLifecycle: unknown): string | null {
  const lifecycle = readBondLifecycle(rawLifecycle);
  if (lifecycle?.phase !== "bid") return null;
  const note = lifecycle.phases.find((p) => p.phase === "bid")?.note ?? null;
  return note !== null && note.startsWith("On hold") ? note : null;
}
